/** The order's Integration tab: the import runs that wrote the order and the webhook /
 *  API requests that touched it — the old overview's Import and API Requests tabs,
 *  unchanged. Each grid fetches when the tab opens. */
import { Alert, Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as ordersApi from '@/api/orders';
import { SectionCard } from '@/components/ReadView';
import { EmbeddedApiRequests } from '@/features/api-requests/EmbeddedApiRequests';
import { TabSkeleton } from '@/features/bookings/record/tabs/TabSkeleton';
import { EmbeddedImportRuns } from '@/features/import-runs/EmbeddedImportRuns';

function ImportRuns({ orderId }: { orderId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['orders', 'imports', orderId],
    queryFn: () => ordersApi.listImportRuns(orderId),
  });
  if (isLoading) return <TabSkeleton label="Loading import runs" />;
  if (error || !data) return <Alert severity="error">Couldn’t load import runs.</Alert>;
  return <EmbeddedImportRuns runs={data} />;
}

function ApiRequests({ orderId }: { orderId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['orders', 'api-requests', orderId],
    queryFn: () => ordersApi.listApiRequests(orderId),
  });
  if (isLoading) return <TabSkeleton label="Loading API requests" />;
  if (error || !data) return <Alert severity="error">Couldn’t load API requests.</Alert>;
  return <EmbeddedApiRequests requests={data} />;
}

export function OrderIntegrationTab({ orderId }: { orderId: number }) {
  return (
    <Box>
      <SectionCard title="Imports">
        <ImportRuns orderId={orderId} />
      </SectionCard>
      <SectionCard title="API requests">
        <ApiRequests orderId={orderId} />
      </SectionCard>
    </Box>
  );
}
